import { useState, useEffect } from 'react';
import { CortexAnimatedLogo } from '@/components/ui/CortexAnimatedLogo';
import { LOADING_STATUS_STEPS } from '@/data/quotes';
import { useLanguage } from '@/hooks/useLanguage';
import { cn } from '@/lib/cn';

interface CortexLoadingScreenProps {
  /** Optional title override */
  title?: string;
  /** Fullscreen overlay vs inline */
  fullScreen?: boolean;
}

export function CortexLoadingScreen({
  title,
  fullScreen = true,
}: CortexLoadingScreenProps) {
  const { t } = useLanguage();

  // Dynamic system status steps loop
  const [statusIndex, setStatusIndex] = useState(0);
  const [isStatusFading, setIsStatusFading] = useState(false);

  // Dynamic status text cycling every 2.2 seconds
  useEffect(() => {
    const statusTimer = setInterval(() => {
      setIsStatusFading(true);
      setTimeout(() => {
        setStatusIndex((prev) => (prev + 1) % LOADING_STATUS_STEPS.length);
        setIsStatusFading(false);
      }, 200);
    }, 2200);

    return () => clearInterval(statusTimer);
  }, []);

  const currentStatusText = title
    ? t(title)
    : t(LOADING_STATUS_STEPS[statusIndex]);

  const content = (
    <div className="flex flex-col items-center justify-center text-center select-none animate-fade-in py-6">
      
      {/* 1. Animated Logo Mark */}
      <div className="mb-5 flex items-center justify-center">
        <CortexAnimatedLogo size={114} className="text-ink-900" />
      </div>

      {/* 2. Brand Identity Baseline Alignment */}
      <div className="flex items-baseline justify-center gap-2 mb-4">
        <span className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink-900 leading-none">
          CORTEX
        </span>
        <span className="text-[11px] sm:text-xs font-bold tracking-widest text-[#B87445] uppercase leading-none">
          BY VIREON
        </span>
      </div>

      {/* 3. Refined Status Line */}
      <div className="flex items-center justify-center gap-2.5 min-h-[28px]">
        <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse shrink-0 shadow-sm shadow-brand-500/50" />
        <span
          className={cn(
            "text-sm sm:text-base font-sans font-medium text-ink-600 tracking-wide transition-opacity duration-200",
            isStatusFading ? "opacity-0" : "opacity-100"
          )}
        >
          {currentStatusText}
        </span>
      </div>

    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-surface-bg/98 backdrop-blur-md transition-all duration-300">
        {content}
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center py-16 w-full">
      {content}
    </div>
  );
}
